// ─── API Helper ─────────────────────────────────────────────
// Centralized API calls for the TMS EHR frontend.
// All methods return JSON responses. Token management handled automatically.

// API_BASE selection priority:
//   1. Build-time REACT_APP_API_BASE (set by Render for cloud builds)
//      -> cloud frontend on Render points at the Render backend
//   2. Production (served by Nginx on 443) -> relative /api path
//      -> Mac Mini build behind Nginx, same host, no CORS
//   3. Development (React dev server on 3000) -> absolute URL to port 5001
//      -> local dev against a Flask server on the same machine
const BUILD_TIME_API_BASE = process.env.REACT_APP_API_BASE;
const IS_PRODUCTION = window.location.port === "" || window.location.port === "443";
const API_BASE = BUILD_TIME_API_BASE
  ? BUILD_TIME_API_BASE
  : IS_PRODUCTION
    ? "/api"
    : `http://${window.location.hostname}:5001/api`;

// CLINIC_SUBDOMAIN — resolved once at module load from window.location.hostname
// and sent as the X-Clinic-Subdomain header on every API call so the backend
// can resolve the tenant when the request hostname is api.tmssuite.com.
//
// Rules:
//   - hostname has 3+ dotted parts AND
//   - leftmost label matches /^[a-z][a-z0-9-]{1,62}$/ (after lowercasing) AND
//   - leftmost label is NOT "api" or "www"
//   → use lowercased leftmost label.
//   Otherwise (Mac Mini IP, localhost, dev hostnames, api.tmssuite.com itself,
//   www-prefixed hosts) → null. The backend's host-based fallback then handles
//   IP/localhost (Fox), and api.tmssuite.com without header is a backend 400
//   we never want to hit from a real clinic frontend.
const CLINIC_SUBDOMAIN = (() => {
  // Build-time override via REACT_APP_CLINIC_SUBDOMAIN. Used on Pi (and any
  // future on-prem) deployments where the LAN hostname (e.g. tms-pi-fox.local
  // or an IP) doesn't carry the clinic subdomain. Set this in .env.production
  // before `npm run build`. Cloud builds leave it unset and fall through to
  // the hostname-derived path below.
  const envOverride = (process.env.REACT_APP_CLINIC_SUBDOMAIN || "").toLowerCase();
  if (envOverride && /^[a-z][a-z0-9-]{1,62}$/.test(envOverride)) {
    return envOverride;
  }
  const host = (window.location.hostname || "").toLowerCase();
  const parts = host.split(".");
  if (parts.length < 3) return null;
  const label = parts[0];
  if (label === "api" || label === "www") return null;
  if (!/^[a-z][a-z0-9-]{1,62}$/.test(label)) return null;
  return label;
})();

// ─── Device pairing — two-tier auth ──────────────────────────
// device_id + device_token persist in localStorage so they survive PWA
// reload. Sent as X-Device-Id + X-Device-Token on every request.
// Cleared only on explicit unpair/revoke (NOT on logout — the iPad
// stays paired across operator sessions).
const DEVICE_ID_KEY = "tms_device_id";
const DEVICE_TOKEN_KEY = "tms_device_token";

function _readDeviceCreds() {
  try {
    return {
      id: localStorage.getItem(DEVICE_ID_KEY) || null,
      token: localStorage.getItem(DEVICE_TOKEN_KEY) || null,
    };
  } catch {
    return { id: null, token: null };
  }
}

export function setDeviceCreds(id, token) {
  try {
    localStorage.setItem(DEVICE_ID_KEY, id);
    localStorage.setItem(DEVICE_TOKEN_KEY, token);
  } catch {}
}

export function clearDeviceCreds() {
  try {
    localStorage.removeItem(DEVICE_ID_KEY);
    localStorage.removeItem(DEVICE_TOKEN_KEY);
  } catch {}
}

export function hasDeviceCreds() {
  const c = _readDeviceCreds();
  return !!(c.id && c.token);
}

export function getDeviceId() {
  return _readDeviceCreds().id;
}

let _token = null;
let _tokenExp = null;  // Token expiry timestamp (seconds)
let _onAuthExpired = null;  // Callback for auth expiry (set by App.js)

function setToken(token) {
  _token = token;
  // Decode JWT to get expiry (JWT payload is base64url-encoded)
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      _tokenExp = payload.exp;
    } catch { _tokenExp = null; }
  } else {
    _tokenExp = null;
  }
}

export function setOnAuthExpired(callback) {
  _onAuthExpired = callback;
}

// _authHeaders — returns an object of headers to merge into every API
// request. Built fresh on each call so the current _token is captured.
//   - "Authorization": Bearer token, only when _token is set.
//   - "X-Clinic-Subdomain": clinic subdomain, only when CLINIC_SUBDOMAIN
//     is non-null. The backend uses this to resolve the tenant when the
//     request hostname is api.tmssuite.com.
//   - "X-Device-Id" + "X-Device-Token": device-factor auth, only when
//     this iPad has been paired (creds in localStorage).
// No Content-Type — caller decides (or omits, e.g. for FormData).
function _authHeaders() {
  const headers = {};
  if (_token) {
    headers["Authorization"] = `Bearer ${_token}`;
  }
  if (CLINIC_SUBDOMAIN) {
    headers["X-Clinic-Subdomain"] = CLINIC_SUBDOMAIN;
  }
  const dev = _readDeviceCreds();
  if (dev.id && dev.token) {
    headers["X-Device-Id"] = dev.id;
    headers["X-Device-Token"] = dev.token;
  }
  return headers;
}

async function _maybeRefreshToken() {
  // S3: Refresh token if it expires within 10 minutes
  if (!_token || !_tokenExp) return;
  const now = Math.floor(Date.now() / 1000);
  const remaining = _tokenExp - now;
  if (remaining > 600) return;  // More than 10 min left, no refresh needed
  if (remaining <= 0) return;   // Already expired, let 401 handler deal with it

  try {
    // Direct fetch, NOT _fetchRaw — we must avoid the _maybeRefreshToken
    // preflight loop. _authHeaders() still gives us auth + clinic headers.
    const resp = await fetch(`${API_BASE}/auth/refresh`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ..._authHeaders(),
      },
    });
    if (resp.ok) {
      const data = await resp.json();
      setToken(data.token);
    }
  } catch {
    // Refresh failed — token will expire naturally
  }
}

// Keepalive: force a token refresh check. Called by App.js during active
// treatment sessions when no other API calls are being made.
export async function refreshTokenKeepalive() {
  if (!_token || !_tokenExp) return;
  // Force refresh if within 15 minutes of expiry (wider window than normal
  // _maybeRefreshToken so we never cut it close during a long treatment)
  const now = Math.floor(Date.now() / 1000);
  const remaining = _tokenExp - now;
  if (remaining <= 0) return;  // Already expired
  if (remaining > 900) return; // More than 15 min left, skip

  try {
    // Direct fetch, NOT _fetchRaw — same preflight-loop reason as
    // _maybeRefreshToken. _authHeaders() still gives us auth + clinic.
    const resp = await fetch(`${API_BASE}/auth/refresh`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ..._authHeaders(),
      },
    });
    if (resp.ok) {
      const data = await resp.json();
      setToken(data.token);
    }
  } catch {
    // Silent failure — will retry on next interval
  }
}

async function _fetch(path, methodOrOptions = {}, body = undefined) {
  // Auto-refresh token before request if nearing expiry
  await _maybeRefreshToken();

  // Support shorthand: _fetch("/path", "POST", data) or _fetch("/path", { method, body })
  let options = {};
  if (typeof methodOrOptions === "string") {
    options = { method: methodOrOptions };
    if (body !== undefined) options.body = JSON.stringify(body);
  } else {
    options = methodOrOptions;
  }

  // Header merge order:
  //   default Content-Type → caller-provided headers → auth/clinic.
  // Auth/clinic headers always win so callers can't accidentally clobber them.
  const headers = {
    "Content-Type": "application/json",
    ...(options.headers || {}),
    ..._authHeaders(),
  };

  const resp = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  // Handle 401 (token expired or invalid)
  if (resp.status === 401 && _token) {
    // Token expired — caller should redirect to login
    const data = await resp.json().catch(() => ({ error: "Unauthorized" }));
    if (_onAuthExpired) _onAuthExpired();
    throw { status: 401, ...data };
  }

  const data = await resp.json().catch(() => null);

  if (!resp.ok) {
    throw { status: resp.status, ...(data || { error: "Request failed" }) };
  }

  return data;
}

// _fetchRaw — like _fetch but returns the raw Response object so callers
// can call .blob(), .json(), .text(), etc. themselves. Used by binary
// download endpoints (PDF/CSV reports & exports), FormData uploads
// (file imports, OCR scans, logo uploads), and anything else where
// _fetch's "always JSON in / always JSON out" assumption doesn't fit.
//
// Inherits the full request flow:
//   - _maybeRefreshToken() preflight
//   - auth + clinic headers via _authHeaders()
//   - 401 → call _onAuthExpired() and throw
//   - non-OK → throw { status, ...errorJson }; falls back to text body
//     for blob endpoints whose error responses aren't JSON-shaped
//
// Differs from _fetch:
//   - No default Content-Type. Caller must set it explicitly when
//     sending JSON; for FormData, the browser sets multipart boundary
//     automatically and the caller must NOT set Content-Type at all.
//   - Returns the raw Response on success; caller picks the body codec.
async function _fetchRaw(path, options = {}) {
  await _maybeRefreshToken();

  const headers = {
    ...(options.headers || {}),
    ..._authHeaders(),
  };

  const resp = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  // 401 handling parallels _fetch.
  if (resp.status === 401 && _token) {
    const data = await resp.json().catch(() => ({ error: "Unauthorized" }));
    if (_onAuthExpired) _onAuthExpired();
    throw { status: 401, ...data };
  }

  if (!resp.ok) {
    // Try JSON first (Flask's jsonify error responses); fall back to
    // text for blob/streaming endpoints whose errors are plain text.
    let errBody;
    try {
      errBody = await resp.json();
    } catch {
      const text = await resp.text().catch(() => "");
      errBody = { error: text || "Request failed" };
    }
    throw { status: resp.status, ...errBody };
  }

  return resp;
}

// ─── Auth ────────────────────────────────────────────────────

// login(userId, pin)  — new two-tier flow, narrows bcrypt to one user
// login(pin)          — legacy single-arg form, scans all clinic users
//                       (still works in backend permissive mode)
//
// The two-arg form is what every new caller should use. The legacy
// form is preserved so we can ship the backend change ahead of all
// frontend updates without breaking anything.
export async function login(userIdOrPin, pinMaybe) {
  const body =
    pinMaybe === undefined
      ? { pin: userIdOrPin }
      : { user_id: userIdOrPin, pin: pinMaybe };
  const data = await _fetch("/auth/login", {
    method: "POST",
    body: JSON.stringify(body),
  });
  _token = data.token;
  return data;
}

// Pre-login operator picker. Requires X-Device-Id + X-Device-Token in
// enforce mode; in permissive mode the backend still returns the list.
export async function getOperators() {
  return _fetch("/auth/users");
}

// ─── Device pairing + management (two-tier auth) ─────────────

// Admin generates a 6-digit pair code. JWT-protected, MD-only.
export async function devicePairInit(displayName, force = false) {
  return _fetch("/devices/pair-init", {
    method: "POST",
    body: JSON.stringify({ display_name: displayName, force }),
  });
}

// Unpaired iPad redeems a pair code. NO JWT. On success, persists the
// returned device_token in localStorage so subsequent requests carry it.
export async function devicePairComplete(code) {
  const data = await _fetch("/devices/pair-complete", {
    method: "POST",
    body: JSON.stringify({ code }),
  });
  if (data && data.device_id && data.device_token) {
    setDeviceCreds(data.device_id, data.device_token);
  }
  return data;
}

// Admin: list devices for this clinic. Paginated by the backend.
export async function deviceList(includeRevoked = false) {
  const qs = includeRevoked ? "?include_revoked=true" : "";
  return _fetch(`/devices${qs}`);
}

// Admin: revoke a device. Instant lockout via the JWT blocklist hook.
export async function deviceRevoke(deviceId, reason = "") {
  return _fetch(`/devices/${deviceId}/revoke`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

export async function logout() {
  try {
    await _fetch("/auth/logout", { method: "POST" });
  } catch (e) {
    // Ignore errors on logout
  }
  _token = null;
}

// ─── Users ───────────────────────────────────────────────────

export async function getUsers() {
  return _fetch("/users");
}

export async function createUser(data) {
  return _fetch("/users", { method: "POST", body: JSON.stringify(data) });
}

export async function updateUser(id, data) {
  return _fetch(`/users/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

export async function updatePermissions(id, permissions) {
  return _fetch(`/users/${id}/permissions`, { method: "PUT", body: JSON.stringify(permissions) });
}

export async function deactivateUser(id) {
  return _fetch(`/users/${id}`, { method: "DELETE" });
}


// ─── Patients ────────────────────────────────────────────────

export async function getPatients(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return _fetch(`/patients${qs ? "?" + qs : ""}`);
}


export async function createPatient(data) {
  return _fetch("/patients", { method: "POST", body: JSON.stringify(data) });
}

export async function updatePatient(id, data) {
  return _fetch(`/patients/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

// ─── Providers ───────────────────────────────────────────────

export async function getProviders() {
  return _fetch("/providers");
}

export async function createProvider(data) {
  return _fetch("/providers", { method: "POST", body: JSON.stringify(data) });
}

export async function updateProvider(id, data) {
  return _fetch(`/providers/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

export async function deleteProvider(id) {
  return _fetch(`/providers/${id}`, { method: "DELETE" });
}

// ─── Protocols ───────────────────────────────────────────────

export async function getProtocols(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return _fetch(`/protocols${qs ? "?" + qs : ""}`);
}


export async function createProtocol(data) {
  return _fetch("/protocols", { method: "POST", body: JSON.stringify(data) });
}

export async function updateProtocol(id, data) {
  return _fetch(`/protocols/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

export async function copyProtocol(id, data = {}) {
  return _fetch(`/protocols/${id}/copy`, { method: "POST", body: JSON.stringify(data) });
}

export async function deleteProtocol(id) {
  return _fetch(`/protocols/${id}`, { method: "DELETE" });
}

export async function reorderProtocols(order) {
  return _fetch("/protocols/reorder", "POST", { order });
}

// ─── Settings ────────────────────────────────────────────────

export async function getSettings(category) {
  return _fetch(`/settings/${category}`);
}

export async function createSetting(category, data) {
  return _fetch(`/settings/${category}`, { method: "POST", body: JSON.stringify(data) });
}

export async function updateSetting(category, id, data) {
  return _fetch(`/settings/${category}/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

export async function getClinic() {
  return _fetch("/settings/clinic");
}

export async function updateClinic(data) {
  return _fetch("/settings/clinic", { method: "PUT", body: JSON.stringify(data) });
}

// ─── Treatments ──────────────────────────────────────────────

export async function getPatientTreatments(patientId, params = {}) {
  const qs = new URLSearchParams(params).toString();
  return _fetch(`/patients/${patientId}/treatments${qs ? "?" + qs : ""}`);
}


export async function assignTreatment(data) {
  return _fetch("/treatments", { method: "POST", body: JSON.stringify(data) });
}

export async function updateTreatment(id, data) {
  return _fetch(`/treatments/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

// Save MT measurement (value, ratio, coil, spot) to the treatment
// without creating a session record. Backed by PUT /api/treatments/:id/mt,
// gated on execute_session permission. Used by MTDeterminationScreen so
// the measurement persists even if the operator navigates away before
// logging the session.
export async function saveMT(treatmentId, { lastMtValue, lastTreatmentRatioPct, mtCoil, mtSpot }) {
  const body = {};
  if (lastMtValue != null) body.last_mt_value = lastMtValue;
  if (lastTreatmentRatioPct != null) body.last_treatment_ratio_pct = lastTreatmentRatioPct;
  if (mtCoil !== undefined) body.mt_coil = mtCoil;
  if (mtSpot !== undefined) body.mt_spot = mtSpot;
  return _fetch(`/treatments/${treatmentId}/mt`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

// ─── Sessions ────────────────────────────────────────────────

export async function getPatientSessions(patientId, params = {}) {
  const qs = new URLSearchParams(params).toString();
  return _fetch(`/patients/${patientId}/sessions${qs ? "?" + qs : ""}`);
}


export async function createSession(data) {
  return _fetch("/sessions", { method: "POST", body: JSON.stringify(data) });
}

export async function createManualSession(data) {
  return _fetch("/sessions/manual", { method: "POST", body: JSON.stringify(data) });
}

export async function discardSession(data) {
  return _fetch("/sessions/discard", { method: "POST", body: JSON.stringify(data) });
}


// ─── Reminders ───────────────────────────────────────────────

export async function getReminders(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return _fetch(`/reminders${qs ? "?" + qs : ""}`);
}

export async function createReminder(data) {
  return _fetch("/reminders", { method: "POST", body: JSON.stringify(data) });
}

export async function updateReminder(id, data) {
  return _fetch(`/reminders/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

export async function clearReminder(id) {
  return _fetch(`/reminders/${id}/clear`, { method: "POST" });
}

export async function deleteReminder(id) {
  return _fetch(`/reminders/${id}`, { method: "DELETE" });
}


// ─── Assessments ────────────────────────────────────────────

export async function getAssessmentScales() {
  return _fetch("/assessment-scales?include_questions=true");
}


export async function createAssessmentScale(data) {
  return _fetch("/assessment-scales", { method: "POST", body: JSON.stringify(data) });
}

export async function updateAssessmentScale(id, data) {
  return _fetch(`/assessment-scales/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

export async function copyAssessmentScale(id, data = {}) {
  return _fetch(`/assessment-scales/${id}/copy`, { method: "POST", body: JSON.stringify(data) });
}

export async function deleteAssessmentScale(id) {
  return _fetch(`/assessment-scales/${id}`, { method: "DELETE" });
}

export async function createAssessment(data) {
  return _fetch("/assessments", { method: "POST", body: JSON.stringify(data) });
}


export async function getPatientAssessments(patientId, params = {}) {
  const qs = new URLSearchParams(params).toString();
  return _fetch(`/patients/${patientId}/assessments${qs ? "?" + qs : ""}`);
}

export async function generateAssessmentLink(data) {
  return _fetch("/assessments/generate-link", { method: "POST", body: JSON.stringify(data) });
}

// ─── Amendments ─────────────────────────────────────────────

export async function amendSession(sessionId, data) {
  return _fetch(`/sessions/${sessionId}/amend`, { method: "POST", body: JSON.stringify(data) });
}

export async function amendAssessment(adminId, data) {
  return _fetch(`/assessments/${adminId}/amend`, { method: "POST", body: JSON.stringify(data) });
}

export async function voidSession(sessionId, data) {
  return _fetch(`/sessions/${sessionId}/void`, { method: "POST", body: JSON.stringify(data) });
}

export async function voidAssessment(adminId, data) {
  return _fetch(`/assessments/${adminId}/void`, { method: "POST", body: JSON.stringify(data) });
}

// ─── Reports & Export (Phase 3) ─────────────────────────────

export async function generateInsuranceReport(patientId, data = {}) {
  const resp = await _fetchRaw(`/reports/insurance/${patientId}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  return resp.blob();
}

export async function generateProviderReport(patientId, data = {}) {
  const resp = await _fetchRaw(`/reports/provider/${patientId}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  return resp.blob();
}

export async function getNarrativeDraft(patientId, data = {}) {
  return _fetch(`/reports/narrative-draft/${patientId}`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function exportSessions(params = {}) {
  const qs = new URLSearchParams(params).toString();
  const resp = await _fetchRaw(`/export/sessions${qs ? "?" + qs : ""}`);
  return resp.blob();
}

export async function exportAssessments(params = {}) {
  const qs = new URLSearchParams(params).toString();
  const resp = await _fetchRaw(`/export/assessments${qs ? "?" + qs : ""}`);
  return resp.blob();
}

// ── Import ──────────────────────────────────────────────
export async function importSessions(file) {
  const formData = new FormData();
  formData.append("file", file);
  // No Content-Type header — browser sets multipart boundary automatically.
  const resp = await _fetchRaw("/import/sessions", {
    method: "POST",
    body: formData,
  });
  return resp.json();
}

export async function importAssessments(file) {
  const formData = new FormData();
  formData.append("file", file);
  const resp = await _fetchRaw("/import/assessments", {
    method: "POST",
    body: formData,
  });
  return resp.json();
}

export async function downloadSessionTemplate() {
  const resp = await _fetchRaw("/import/template/sessions");
  return resp.blob();
}

export async function downloadAssessmentTemplate() {
  const resp = await _fetchRaw("/import/template/assessments");
  return resp.blob();
}

// ── OCR ─────────────────────────────────────────────────
export async function ocrStatus() {
  return _fetch("/providers/ocr-status");
}

export async function ocrScanReferral(imageFile) {
  const formData = new FormData();
  formData.append("image", imageFile);
  const resp = await _fetchRaw("/providers/ocr-scan", {
    method: "POST",
    body: formData,
  });
  return resp.json();
}

// ── Google Calendar ─────────────────────────────────────
export function calendarStatus() { return _fetch("/calendar/status"); }
export function calendarGetAuthUrl() { return _fetch("/calendar/auth-url"); }
export function calendarDisconnect() { return _fetch("/calendar/disconnect", "POST"); }
export function calendarListCalendars() { return _fetch("/calendar/calendars"); }
export function calendarSaveSelectedCalendar(data) { return _fetch("/calendar/selected-calendar", "POST", data); }
export function calendarGetEvents(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return _fetch(`/calendar/events${qs ? "?" + qs : ""}`);
}
export function calendarCreateEvent(data) { return _fetch("/calendar/events", "POST", data); }
export function calendarDeleteEvent(eventId, calendarId) {
  const qs = calendarId ? `?calendar_id=${calendarId}` : "";
  return _fetch(`/calendar/events/${eventId}${qs}`, "DELETE");
}

export async function uploadLogo(file) {
  const formData = new FormData();
  formData.append("logo", file);
  const resp = await _fetchRaw("/settings/logo", {
    method: "POST",
    body: formData,
  });
  return resp.json();
}

export async function deleteLogo() {
  return _fetch("/settings/logo", { method: "DELETE" });
}

export function getLogoUrl() {
  return `${API_BASE}/settings/logo`;
}

// ─── Device Integration (Phase 4) ────────────────────────────

export function deviceConnect(port) {
  return _fetch("/device/connect", { method: "POST", body: JSON.stringify({ port: port || undefined }) });
}

export function deviceDisconnect() {
  return _fetch("/device/disconnect", { method: "POST" });
}

export function deviceStatus() {
  return _fetch("/device/status");
}

export function deviceTelemetry(treatmentId) {
  const qs = treatmentId ? `?treatment_id=${treatmentId}` : "";
  return _fetch(`/device/telemetry${qs}`);
}

export function deviceArm(sessionId) {
  return _fetch("/device/arm", { method: "POST", body: JSON.stringify({ session_id: sessionId || undefined }) });
}

export function deviceDisarm(sessionId) {
  return _fetch("/device/disarm", { method: "POST", body: JSON.stringify({ session_id: sessionId || undefined }) });
}

export function deviceSetAmplitude(amplitudePct, sessionId) {
  return _fetch("/device/set-amplitude", { method: "POST", body: JSON.stringify({ amplitude_pct: amplitudePct, session_id: sessionId || undefined }) });
}

export function deviceSetWaveform(waveform, sessionId) {
  return _fetch("/device/set-waveform", { method: "POST", body: JSON.stringify({ waveform, session_id: sessionId || undefined }) });
}

export function deviceLoadParams(treatmentId, intensityPct, sessionId) {
  return _fetch("/device/load-params", { method: "POST", body: JSON.stringify({ treatment_id: treatmentId, intensity_pct: intensityPct, session_id: sessionId || undefined }) });
}

export function deviceFire(sessionId) {
  return _fetch("/device/fire", { method: "POST", body: JSON.stringify({ session_id: sessionId || undefined }) });
}

export function deviceStartTreatment(sessionId) {
  return _fetch("/device/start-treatment", { method: "POST", body: JSON.stringify({ session_id: sessionId || undefined }) });
}

export function deviceStopTreatment(sessionId) {
  return _fetch("/device/stop-treatment", { method: "POST", body: JSON.stringify({ session_id: sessionId || undefined }) });
}

export function devicePauseTreatment(sessionId) {
  return _fetch("/device/pause-treatment", { method: "POST", body: JSON.stringify({ session_id: sessionId || undefined }) });
}

export function deviceResumeTreatment(sessionId, trainsRemaining) {
  return _fetch("/device/resume-treatment", { method: "POST", body: JSON.stringify({ session_id: sessionId || undefined, trains_remaining: trainsRemaining }) });
}

export function deviceDeliveredParams() {
  return _fetch("/device/delivered-params");
}

export function deviceLoadMTProtocol(sessionId) {
  return _fetch("/device/load-mt-protocol", { method: "POST", body: JSON.stringify({ session_id: sessionId || undefined }) });
}

export function deviceLoadProtocolDirect(protocolId, intensityPct, sessionId) {
  return _fetch("/device/load-protocol-direct", { method: "POST", body: JSON.stringify({ protocol_id: protocolId, intensity_pct: intensityPct, session_id: sessionId || undefined }) });
}

export function deviceLoadCustomParams(params, sessionId) {
  return _fetch("/device/load-custom-params", { method: "POST", body: JSON.stringify({ ...params, session_id: sessionId || undefined }) });
}

// ─── Device Lock ─────────────────────────────────────────────

export function deviceAcquireLock(sessionId, patientName) {
  return _fetch("/device/lock", { method: "POST", body: JSON.stringify({ session_id: sessionId, patient_name: patientName }) });
}

export function deviceReleaseLock(sessionId) {
  return _fetch("/device/unlock", { method: "POST", body: JSON.stringify({ session_id: sessionId }) });
}

// ─── Setup (first-run) ──────────────────────────────────────

export function getSetupStatus() {
  return _fetch("/setup/status");
}

export function createInitialMD(data) {
  return _fetch("/setup/create-md", "POST", data);
}


// ─── Auth Config ─────────────────────────────────────────────

export function getAuthConfig() {
  return _fetch("/auth/config");
}
